import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { MemberDetailPanel } from '@components/MemberDetailPanel';
import {
  BrowseToolbar, SpacerRow, interleaveDetail, useBrowseVirtual,
} from '@components/lookup/browse-kit';
import { GridFrame, GridHeadRow, GridToolbar, gridCell } from '@components/grid/CareGrid';
import { useGridView, type GridColumn } from '@components/grid/useGridView';
import { multiSort } from '@lib/grid';
import { SlicerRail } from '@components/lookup/SlicerRail';
import { ShippingLookupSection } from '@components/lookup/ShippingLookupSection';
import { enrouteExport, enrouteList, memberDetail, shippingDetail, type LookupTab } from '@lib/care-api';
import { hayIncludes } from '@lib/hay';
import {
  EMPTY_LOOKUP_FILTERS, activeFilterCount, buildEnrouteAxes, cohortLabel, matchEnrouteOps, type LookupFilters,
} from '@lib/lookup-axes';
import { fmtMemberId } from '@lib/member-id';
import type { EmptyReason, EnrouteKitRow, EnrouteListResp, EnroutePull, MemberDetailResp, ShippingScan } from '@lib/care-model';
import { EMPTY_REASON_META, emptyRow, emptyTone } from '@lib/care-model';
import { isValidReason, useSessionReason } from '@lib/useSessionReason';

/**
 * EnrouteSection - the Enroute tab (row 418, William 19 Sep 2026: "make this lookup just like the one
 * for the main portal"), the Portal's EnrouteSection (v1 MemberLookup.tsx line 3658) on Care's data:
 *
 *   KITS       population-wide USPS shipment activity, one row per kit, the Portal's columns in the
 *              Portal's order: Member, Cohort, Market, Barcode, Shipped, Outbound, Delivered, Return,
 *              Sample shipped, Received, Resulted, behind a leading toggle column. The whole table
 *              loads once (POST /api/care/enroute-list), then search (member id, name, barcode,
 *              tracking number), the slicer rail and the sort are client-side. A row expands to the
 *              kit's USPS scan timeline and the member's full record.
 *   PULL CARD  the database's build time, the USPS pull time, and the funnel over the whole table
 *              (total, delivered, sample shipped, received, exceptions, resulted), the Portal's
 *              DataPullBox in Care's paper.
 *   LETTERS    the second sub-tab, the Portal's Kits / Letters toggle: the mailed letters, which are
 *              Care's former Shipping tab narrowed to letters (ShippingLookupSection with kind 'letter').
 *   THE GATE   the Portal serves Enroute WITHOUT a reason (shipment facts). Care's rule stands: the
 *              rows carry member names, so the read sits behind the session reason and is audited.
 *   EXPORT     exactly the rows on screen, behind the page's export-scoped re-prompt.
 */

type ColKey = 'member' | 'cohort' | 'market' | 'barcode' | 'shipped' | 'outbound' | 'delivered' | 'return' | 'sampleShipped' | 'received' | 'resulted';

/** The Enroute kits columns in the Portal's order and wording (EnrouteSection COLS). */
export const ENROUTE_COLUMNS: ReadonlyArray<{ key: ColKey; label: string }> = [
  { key: 'member', label: 'Member' },
  { key: 'cohort', label: 'Cohort' },
  { key: 'market', label: 'Market' },
  { key: 'barcode', label: 'Barcode' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'outbound', label: 'Outbound' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'return', label: 'Return' },
  { key: 'sampleShipped', label: 'Sample shipped' },
  { key: 'received', label: 'Received' },
  { key: 'resulted', label: 'Resulted' },
];

/** The Portal's Enroute search: member id, name, barcode, both tracking numbers. */
export function enrouteHay(r: EnrouteKitRow): string {
  return `${r.member_id} ${r.full_name} ${r.barcode_id} ${r.outbound_tracking} ${r.return_tracking}`.toLowerCase();
}

/** "MM/DD/YYYY" (the kit ship_date) or ISO to a comparable "YYYY-MM-DD". */
export function toIsoDate(v: string | null | undefined): string {
  if (!v) return '';
  const s = String(v).trim();
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);
  if (us) return `${us[3]}-${us[1]!.padStart(2, '0')}-${us[2]!.padStart(2, '0')}`;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return iso ? `${iso[1]}-${iso[2]}-${iso[3]}` : '';
}

function sortValue(r: EnrouteKitRow, key: ColKey): string {
  switch (key) {
    case 'member': return (r.full_name || r.member_id || '').toLowerCase();
    case 'cohort': return (r.cohort || '').toLowerCase();
    case 'market': return (r.market || '').toLowerCase();
    case 'barcode': return r.barcode_id || '';
    case 'shipped': return toIsoDate(r.ship_date);
    case 'outbound': return (r.outbound_status_category || '').toLowerCase();
    case 'delivered': return r.delivered_to_member_ts || '';
    case 'return': return (r.return_status_category || '').toLowerCase();
    case 'sampleShipped': return r.sample_shipped_ts || '';
    case 'received': return r.sample_received_ts || '';
    case 'resulted': return r.resulted_ts || '';
  }
}

/** A scan timestamp for a cell: the day and time without a day shift (no new Date()). */
function fmtScanTs(v: string): string {
  const s = (v ?? '').trim();
  if (!s) return '';
  const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(s);
  return m ? `${m[1]} ${m[2]}` : s;
}

const dash = <span className="muted">-</span>;

function statusCell(v: string) {
  if (!v) return dash;
  const rts = /returned to sender/i.test(v);
  return rts ? <span className="pill pill-warning">{v}</span> : <span className="small">{v}</span>;
}

/** The data-pull card: the Portal's DataPullBox, fed by the enroute read. */
export function EnroutePullCard({ pull }: { pull: EnroutePull | null }) {
  if (!pull) return <div className="muted small" style={{ marginBottom: 10 }}>Awaiting the first data pull.</div>;
  const stat = (label: string, n: number) => (
    <div key={label} style={{ textAlign: 'center', minWidth: 72 }}>
      <div style={{ fontSize: 18, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{n.toLocaleString()}</div>
      <div className="micro muted" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
    </div>
  );
  return (
    <div className="card" data-testid="enroute-pull" style={{ padding: '10px 14px', marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ minWidth: 0 }}>
        <div className="micro muted" style={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Data pull</div>
        <div className="small" style={{ marginTop: 2 }}>
          {pull.builtAt ? <>Database built <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtScanTs(pull.builtAt) || pull.builtAt}</span> (UTC)</> : 'Build time not recorded for this campaign'}
        </div>
        <div className="micro muted" style={{ marginTop: 2 }}>
          {pull.trackingRefreshedAt ? `USPS tracking refreshed ${fmtScanTs(pull.trackingRefreshedAt) || pull.trackingRefreshedAt}` : 'USPS pull time not recorded'}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {stat('Kits', pull.totalKits)}
        {stat('Delivered', pull.delivered)}
        {stat('Sample shipped', pull.shipped)}
        {stat('Received', pull.received)}
        {stat('Resulted', pull.resulted)}
        {stat('Exceptions', pull.exceptions)}
      </div>
    </div>
  );
}

export interface EnrouteSectionProps {
  onNeedReason: () => void;
  onRequireExportReason: (proceed: (reason: string) => void) => void;
  onAuditSearch?: (tab: LookupTab, query: string) => void;
  onAuditFilter?: (tab: LookupTab, filters: Record<string, string[]>) => void;
  onJumpToMember?: (memberId: string) => void;
}

export function EnrouteSection(props: EnrouteSectionProps) {
  const [sub, setSub] = useState<'kits' | 'letters'>('kits');
  return (
    <div>
      <div role="tablist" aria-label="Enroute views" style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {(['kits', 'letters'] as const).map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={sub === s}
            className={cn('chip', sub === s && 'selected')}
            onClick={() => setSub(s)}
          >
            {s === 'kits' ? 'Kits' : 'Letters'}
          </button>
        ))}
      </div>
      {sub === 'kits' ? (
        <EnrouteKits {...props} />
      ) : (
        <ShippingLookupSection
          only="letter"
          onNeedReason={props.onNeedReason}
          onRequireExportReason={props.onRequireExportReason}
          onAuditSearch={(q) => props.onAuditSearch?.('letters', q)}
          onAuditFilter={(f) => props.onAuditFilter?.('letters', f)}
        />
      )}
    </div>
  );
}

function EnrouteKits({ onNeedReason, onRequireExportReason, onAuditSearch, onAuditFilter, onJumpToMember }: EnrouteSectionProps) {
  const sessionReason = useSessionReason();
  const reasonValid = isValidReason(sessionReason);

  const [rows, setRows] = useState<EnrouteKitRow[]>([]);
  const [pull, setPull] = useState<EnroutePull | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [total, setTotal] = useState(0);
  const [available, setAvailable] = useState(true);
  const [emptyReason, setEmptyReason] = useState<EmptyReason | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [loadedFor, setLoadedFor] = useState('');

  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [filters, setFilters] = useState<LookupFilters>(EMPTY_LOOKUP_FILTERS);

  const [openBarcode, setOpenBarcode] = useState<string | null>(null);
  const [scans, setScans] = useState<Record<string, ShippingScan[] | 'none' | 'loading'>>({});
  const [memberDetails, setMemberDetails] = useState<Record<string, MemberDetailResp | 'loading' | 'error'>>({});
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState('');

  useEffect(() => {
    if (!reasonValid || loadedFor === sessionReason) return;
    let cancelled = false;
    setLoading(true);
    setLoadErr('');
    void enrouteList(sessionReason)
      .then((r) => {
        if (cancelled) return;
        if (r.ok) {
          const d = r.data as EnrouteListResp;
          const list = Array.isArray(d.rows) ? d.rows : [];
          setRows(list);
          setPull(d.pull ?? null);
          setTruncated(!!d.truncated);
          setTotal(d.total ?? list.length);
          setAvailable(d.available !== false);
          setEmptyReason(d.emptyReason);
          setLoadedFor(sessionReason);
        } else {
          setLoadErr(`Shipment load failed (HTTP ${r.status}).`);
        }
        // The load is over on BOTH branches, set HERE rather than in a finally: a finally runs a
        // microtask after the state above, by which time React may have re-run this effect (loadedFor
        // changed) and flipped `cancelled`, leaving `loading` stuck true (found by the sections test).
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadErr('Shipment load failed.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reasonValid, sessionReason, loadedFor]);

  useEffect(() => {
    const q = deferredSearch.trim();
    if (q) onAuditSearch?.('enroute', q);
  }, [deferredSearch]); // eslint-disable-line react-hooks/exhaustive-deps
  const filtersSeen = useRef(false);
  useEffect(() => {
    if (!filtersSeen.current) {
      filtersSeen.current = true;
      if (activeFilterCount(filters) === 0) return;
    }
    onAuditFilter?.('enroute', filters);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const railFiltered = useMemo(
    () => (Object.keys(filters).length ? rows.filter((r) => matchEnrouteOps(r, filters)) : rows),
    [rows, filters],
  );
  const filtered = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    if (!q) return railFiltered;
    return railFiltered.filter((r) => hayIncludes(enrouteHay(r), q));
  }, [railFiltered, deferredSearch]);
  const axes = useMemo(() => buildEnrouteAxes(rows), [rows]);

  const toggleRow = async (r: EnrouteKitRow) => {
    if (openBarcode === r.barcode_id) {
      setOpenBarcode(null);
      return;
    }
    setOpenBarcode(r.barcode_id);
    if (scans[r.barcode_id] === undefined) {
      setScans((prev) => ({ ...prev, [r.barcode_id]: 'loading' }));
      try {
        const d = await shippingDetail(r.barcode_id);
        setScans((prev) => ({ ...prev, [r.barcode_id]: d.available && d.scans.length ? d.scans : 'none' }));
      } catch {
        setScans((prev) => ({ ...prev, [r.barcode_id]: 'none' }));
      }
    }
    if (r.member_id && memberDetails[r.member_id] === undefined) {
      setMemberDetails((prev) => ({ ...prev, [r.member_id]: 'loading' }));
      try {
        const md = await memberDetail(r.member_id, sessionReason);
        setMemberDetails((prev) => ({
          ...prev,
          [r.member_id]: md.ok && (md.data as MemberDetailResp).found ? (md.data as MemberDetailResp) : 'error',
        }));
      } catch {
        setMemberDetails((prev) => ({ ...prev, [r.member_id]: 'error' }));
      }
    }
  };

  const nowrap = { whiteSpace: 'nowrap' } as const;
  // The toggle column lives inside a memoized `columns` array (row 356's grid habit), whose deps are
  // the open row and the loaded details, NOT the session reason. A closure captured there before the
  // grant would call the reveal with an EMPTY reason and be refused by the server (found by the shell
  // test on 19 Sep 2026: memberDetail called with ''). The column therefore calls through a ref that
  // always holds the current toggleRow.
  const toggleRef = useRef(toggleRow);
  toggleRef.current = toggleRow;
  const columns = useMemo<GridColumn<EnrouteKitRow>[]>(() => [
    {
      key: 'toggle', label: '', menuLabel: 'Expand', locked: true, stopRowClick: true, defaultWidth: 36,
      thStyle: { width: 36 }, tdStyle: { textAlign: 'center' },
      render: (r) => {
        const open = openBarcode === r.barcode_id;
        return (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ padding: '0 6px' }}
            aria-expanded={open}
            aria-label={open ? 'Hide shipment detail' : 'Show full shipment history'}
            title={open ? 'Hide shipment detail' : 'Show full shipment history'}
            onClick={() => void toggleRef.current(r)}
          >
            {open ? '▾' : '▸'}
          </button>
        );
      },
    },
    {
      key: 'member', label: 'Member', tdStyle: nowrap, sortValue: (r) => sortValue(r, 'member'),
      render: (r) => (
        <>
          <b>{r.full_name || 'member'}</b>
          <span className="muted small" style={{ marginLeft: 6, fontFamily: 'var(--font-mono)' }}>{fmtMemberId(r.member_id)}</span>
        </>
      ),
    },
    { key: 'cohort', label: 'Cohort', tdClassName: 'small', sortValue: (r) => sortValue(r, 'cohort'), render: (r) => (r.cohort ? cohortLabel(r.cohort) : dash) },
    { key: 'market', label: 'Market', tdClassName: 'small', sortValue: (r) => sortValue(r, 'market'), render: (r) => r.market || dash },
    { key: 'barcode', label: 'Barcode', tdClassName: 'small', tdStyle: { ...nowrap, fontFamily: 'var(--font-mono)' }, sortValue: (r) => sortValue(r, 'barcode'), render: (r) => r.barcode_id || dash },
    { key: 'shipped', label: 'Shipped', tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => sortValue(r, 'shipped'), render: (r) => r.ship_date || dash },
    { key: 'outbound', label: 'Outbound', tdStyle: nowrap, sortValue: (r) => sortValue(r, 'outbound'), render: (r) => statusCell(r.outbound_status_category) },
    { key: 'delivered', label: 'Delivered', tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => sortValue(r, 'delivered'), render: (r) => fmtScanTs(r.delivered_to_member_ts) || dash },
    { key: 'return', label: 'Return', tdStyle: nowrap, sortValue: (r) => sortValue(r, 'return'), render: (r) => statusCell(r.return_status_category) },
    { key: 'sampleShipped', label: 'Sample shipped', tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => sortValue(r, 'sampleShipped'), render: (r) => fmtScanTs(r.sample_shipped_ts) || dash },
    { key: 'received', label: 'Received', tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => sortValue(r, 'received'), render: (r) => fmtScanTs(r.sample_received_ts) || dash },
    { key: 'resulted', label: 'Resulted', tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => sortValue(r, 'resulted'), render: (r) => fmtScanTs(r.resulted_ts) || dash },
  ], [openBarcode]); // eslint-disable-line react-hooks/exhaustive-deps
  const view = useGridView('lookup-enroute', columns, { defaultSort: [{ k: 'shipped', asc: false }] });
  const byKey = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns]);
  const sorted = useMemo(
    () => multiSort(filtered, view.sort, (r, k) => byKey.get(k)?.sortValue?.(r)),
    [filtered, view.sort, byKey],
  );
  const span = Math.max(1, view.visible.length);
  const displayItems = useMemo(() => interleaveDetail(sorted, (r) => r.barcode_id === openBarcode), [sorted, openBarcode]);
  const virtual = useBrowseVirtual(displayItems, (it) => `${it.kind}:${it.row.barcode_id}:${it.row.member_id}`, 480);

  // Per-column stage counts over the shown rows (the Portal's header counts).
  const stageCounts = useMemo(() => ({
    delivered: sorted.filter((r) => r.delivered_to_member_ts).length,
    sampleShipped: sorted.filter((r) => r.sample_shipped_ts).length,
    received: sorted.filter((r) => r.sample_received_ts).length,
    resulted: sorted.filter((r) => r.resulted_ts).length,
  }), [sorted]);

  const runExport = async (exportReason: string) => {
    setExporting(true);
    setExportErr('');
    try {
      const { blob, filename } = await enrouteExport(exportReason, sorted);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportErr(e instanceof Error ? e.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  if (!reasonValid) {
    return (
      <div className="note" style={{ marginBottom: 14 }}>
        Provide a reason-for-access to load shipment activity (the rows carry member names, so the read is audited).{' '}
        <button type="button" className="btn btn-sm" onClick={onNeedReason}>Provide reason</button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <EnroutePullCard pull={pull} />
        <BrowseToolbar
          search={search}
          onSearch={setSearch}
          placeholder="Member ID, name, barcode, tracking #"
          ariaLabel="Search shipment activity"
          shown={sorted.length}
          total={rows.length}
          noun="kits"
          right={
            <button
              type="button"
              className="btn btn-sm"
              disabled={exporting || sorted.length === 0}
              title="Export the filtered kits (current view) to Excel"
              onClick={() => {
                setExportErr('');
                onRequireExportReason((exportReason) => { void runExport(exportReason); });
              }}
            >
              {exporting ? 'Exporting...' : 'Export to Excel'}
            </button>
          }
        />
        {exportErr && <div className="note danger" style={{ marginBottom: 14 }}>{exportErr}</div>}
        {loadErr && <div className="note danger" style={{ marginBottom: 14 }}>{loadErr}</div>}
        {!available && !loading && !loadErr && (
          <div className={`note ${emptyTone(emptyReason)}`} style={{ marginBottom: 14 }}>
            {emptyReason === 'capability-absent'
              ? 'This campaign database carries no kit tracking (not part of this program).'
              : EMPTY_REASON_META[emptyReason ?? 'source-unreachable'].notice ?? 'Shipment data unavailable.'}
          </div>
        )}
        {truncated && (
          <div className="muted small" style={{ marginBottom: 8 }}>
            Showing the first {rows.length.toLocaleString()} of {total.toLocaleString()} kits (server cap).
          </div>
        )}
        <div className="micro muted" style={{ marginBottom: 6, display: 'flex', gap: 14 }}>
          <span>Delivered {stageCounts.delivered.toLocaleString()}</span>
          <span>Sample shipped {stageCounts.sampleShipped.toLocaleString()}</span>
          <span>Received {stageCounts.received.toLocaleString()}</span>
          <span>Resulted {stageCounts.resulted.toLocaleString()}</span>
          <span>of {sorted.length.toLocaleString()} shown</span>
        </div>

        <GridFrame view={view}>
        <GridToolbar view={view} />
        <div ref={virtual.scrollRef} className="card card-flush" style={{ maxHeight: view.expanded ? 'calc(100vh - 90px)' : '65vh', overflow: 'auto' }}>
          <table className="dt">
            <thead>
              <GridHeadRow view={view} />
            </thead>
            <tbody>
              {displayItems.length === 0 && (
                <tr>
                  <td colSpan={span} className="muted" style={{ textAlign: 'center', padding: '20px 0' }}>
                    {loading ? 'Loading shipment activity...' : emptyRow(emptyReason, search.trim() ? 'No kits match these filters.' : 'No shipment activity found for this campaign.')}
                  </td>
                </tr>
              )}
              <SpacerRow height={virtual.padTop} colSpan={span} />
              {virtual.items.map((vi) => {
                const item = displayItems[vi.index];
                if (!item) return null;
                const r = item.row;
                if (item.kind === 'detail') {
                  const rowScans = scans[r.barcode_id];
                  const md = r.member_id ? memberDetails[r.member_id] : undefined;
                  return (
                    <tr key={vi.key} data-index={vi.index} ref={virtual.measureRef}>
                      <td colSpan={span} style={{ background: 'var(--paper-2)', padding: '12px 16px', height: 'auto' }}>
                        <div style={{ fontWeight: 600 }}>USPS scans <span className="muted">kit {r.barcode_id}</span></div>
                        {(rowScans === undefined || rowScans === 'loading') && <div className="muted small" style={{ padding: '4px 0' }}>Loading shipment history...</div>}
                        {rowScans === 'none' && (
                          <div className="muted small" style={{ padding: '4px 0' }}>No scan-level history for this kit (no scans yet, or no scan table in this campaign).</div>
                        )}
                        {Array.isArray(rowScans) && (
                          <div style={{ overflowX: 'auto', marginTop: 4, marginBottom: 12 }}>
                            <table className="dt">
                              <thead>
                                <tr><th>When</th><th>Direction</th><th>Event</th><th>Where</th><th>Tracking</th></tr>
                              </thead>
                              <tbody>
                                {rowScans.map((sc, j) => (
                                  <tr key={`${sc.event_ts}|${sc.event_code}|${j}`}>
                                    <td className="small" style={nowrap}>{sc.event_ts || dash}</td>
                                    <td className="small">{sc.direction || dash}</td>
                                    <td className="small">{sc.event_type || sc.event_code || dash}</td>
                                    <td className="small">{[sc.city, sc.state, sc.zip].filter(Boolean).join(', ') || dash}</td>
                                    <td className="small" style={nowrap}>{sc.tracking_number || dash}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {md === 'loading' && <div className="muted small">Loading the full record...</div>}
                        {md === 'error' && <div className="muted small">Full record unavailable.</div>}
                        {md && md !== 'loading' && md !== 'error' && (
                          <>
                            {onJumpToMember && (
                              <button type="button" className="btn btn-ghost btn-sm" style={{ marginBottom: 6 }} onClick={() => onJumpToMember(r.member_id)}>
                                Open on the Member tab
                              </button>
                            )}
                            <MemberDetailPanel detail={md} />
                          </>
                        )}
                      </td>
                    </tr>
                  );
                }
                const expanded = openBarcode === r.barcode_id;
                return (
                  <tr
                    key={vi.key}
                    data-index={vi.index}
                    ref={virtual.measureRef}
                    tabIndex={0}
                    aria-expanded={expanded}
                    aria-label={`kit ${r.barcode_id} ${r.full_name || ''}`.trim()}
                    onClick={() => void toggleRow(r)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        void toggleRow(r);
                      }
                    }}
                    style={{ cursor: 'pointer', background: expanded ? 'var(--paper-2)' : undefined }}
                  >
                    {view.visible.map((c) => (
                      <td key={c.key} {...gridCell(view, c, r)}>
                        {c.render(r, vi.index)}
                      </td>
                    ))}
                  </tr>
                );
              })}
              <SpacerRow height={virtual.padBottom} colSpan={span} />
            </tbody>
          </table>
        </div>
        </GridFrame>
      </div>
      <SlicerRail
        axes={axes}
        filters={filters}
        onFilters={setFilters}
        scope={{ shown: railFiltered.length, total: rows.length }}
        noun="kits"
      />
    </div>
  );
}
